import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, FileText, FolderOpen, Loader2, Users } from 'lucide-react';
import { API_URL } from '../../config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import SuperAdminShell from './SuperAdminShell';

export default function SuperAdminOverviewPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    organization_count: 0,
    user_count: 0,
    space_count: 0,
    document_count: 0,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/platform/overview`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load platform overview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tiles = [
    {
      key: 'orgs',
      label: 'Organizations',
      value: data.organization_count,
      icon: Building2,
      color: 'text-violet-600 dark:text-violet-400',
      bg: 'bg-violet-100 dark:bg-violet-900/30',
    },
    {
      key: 'users',
      label: 'Users',
      value: data.user_count,
      icon: Users,
      color: 'text-sky-600 dark:text-sky-400',
      bg: 'bg-sky-100 dark:bg-sky-900/30',
    },
    {
      key: 'spaces',
      label: 'Spaces',
      value: data.space_count,
      icon: FolderOpen,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    },
    {
      key: 'docs',
      label: 'Documents',
      value: data.document_count,
      icon: FileText,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-100 dark:bg-amber-900/30',
    },
  ];

  return (
    <SuperAdminShell
      title="Overview"
      description="High-level counts across all tenants on this DocTree instance."
      testId="super-admin-overview"
    >
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading metrics…
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((t) => (
            <Card
              key={t.key}
              className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {t.label}
                </CardTitle>
                <div className={`rounded-lg p-2 ${t.bg}`}>
                  <t.icon className={`h-4 w-4 ${t.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white">
                  {t.value.toLocaleString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </SuperAdminShell>
  );
}
